/**
 * Testing Module Constants
 *
 * Provides constants, mappings, and utility functions for the Testing Workflow module.
 *
 * Module: 3.0 - System Operations (Issues Manager Phase 2)
 * PRD: docs/prds/PRD-ISSUES-MANAGER.md v2.1
 */

// ============================================================================
// COMPONENT TO MODULE MAPPING
// ============================================================================

/**
 * Maps UI component names to their parent CRM modules.
 * Used for auto-populating the "module" field in inline issue reporting.
 */
export const COMPONENT_TO_MODULE_MAP: Record<string, string> = {
  // Client Manager Module
  "Client Group Modal": "Client Manager",
  "Client Group List": "Client Manager",
  "Client Group Detail Page": "Client Manager",
  "Contact Modal": "Client Manager",
  "Contact List": "Client Manager",
  "Contact Detail Page": "Client Manager",
  "Account Modal": "Client Manager",
  "Account List": "Client Manager",
  "Account Detail Page": "Client Manager",

  // Deal Builder Module
  "Deal Modal": "Deal Builder",
  "Deal List": "Deal Builder",
  "Deal Detail Page": "Deal Builder",
  "Facility Modal": "Deal Builder",
  "Facility List": "Deal Builder",
  "Facility Detail Page": "Deal Builder",
  "Scenario Modal": "Deal Builder",
  "Scenario List": "Deal Builder",
  "Scenario Detail Page": "Deal Builder",

  // Partner & Lender Module
  "Partner Modal": "Partner & Lender",
  "Partner List": "Partner & Lender",
  "Partner Detail Page": "Partner & Lender",

  // General
  "Navigation": "Core UI",
  "Header": "Core UI",
  "Sidebar": "Core UI",
  "Dashboard": "Dashboard",
};

// ============================================================================
// TEST RESULT VISUAL STYLES
// ============================================================================

/**
 * Color codes for test result badges.
 */
export const TEST_RESULT_COLORS: Record<string, string> = {
  pass: "bg-green-100 text-green-800 border-green-300",
  fail: "bg-red-100 text-red-800 border-red-300",
  blocked: "bg-amber-100 text-amber-800 border-amber-300",
  skip: "bg-gray-100 text-gray-600 border-gray-300",
};

/**
 * Display labels for test results.
 */
export const TEST_RESULT_LABELS: Record<string, string> = {
  pass: "✅ Pass",
  fail: "❌ Fail",
  blocked: "🚧 Blocked",
  skip: "⏭️ Skip",
};

// ============================================================================
// QUESTION TYPE TEMPLATES
// ============================================================================

/**
 * Predefined question type templates for common test scenarios.
 * Used by Developer Agent when generating testing checklists.
 */
export const QUESTION_TYPE_TEMPLATES = {
  /**
   * Template: Binary Yes/No question
   */
  YES_NO: {
    type: "yes_no" as const,
    label: "Does the feature work as expected?",
  },

  /**
   * Template: Test result dropdown (Pass, Partial-UI, Partial-Functionality, Fail)
   */
  TEST_RESULT: {
    type: "dropdown" as const,
    label: "Test Result",
    options: ["Pass", "Partial-UI", "Partial-Functionality", "Fail"],
    template: "test_result",
  },

  /**
   * Template: Checkbox for multi-select verification
   */
  CHECKBOX: {
    type: "checkbox" as const,
    label: "Which features work correctly?",
    options: [],  // Populated dynamically by Developer Agent
  },

  /**
   * Template: Combined yes/no + checkbox for detailed testing
   */
  COMBINED_YES_NO_CHECKBOX: [
    {
      type: "yes_no" as const,
      label: "Does modal open?",
    },
    {
      type: "checkbox" as const,
      label: "Which fields are present?",
      options: [],  // Populated dynamically
    },
  ],
};

// ============================================================================
// CHECKLIST STATUS COLORS
// ============================================================================

/**
 * Color codes for checklist progress indicators.
 */
export const CHECKLIST_STATUS_COLORS = {
  NOT_STARTED: "bg-gray-100 text-gray-600",
  IN_PROGRESS: "bg-blue-100 text-blue-800",
  COMPLETE: "bg-green-100 text-green-800",
  FAILED: "bg-red-100 text-red-800",
};

// ============================================================================
// PASS RATE THRESHOLDS
// ============================================================================

/**
 * Thresholds for pass rate color coding.
 */
export const PASS_RATE_THRESHOLDS = {
  EXCELLENT: 95,  // >= 95% → Green
  GOOD: 75,       // >= 75% → Amber
  POOR: 50,       // >= 50% → Orange
                  // < 50% → Red
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Extracts keywords from actual behavior text for auto-generating issue titles.
 *
 * Algorithm:
 * 1. Remove common words (the, a, an, is, are, was, were, etc.)
 * 2. Extract noun phrases and key verbs
 * 3. Capitalize first letter of each word
 * 4. Join with spaces
 *
 * @param actualBehavior - User-entered actual behavior text
 * @returns Extracted keywords as title string
 *
 * @example
 * extractKeywords("The group type dropdown is not present in the modal")
 * // Returns: "Group Type Dropdown Not Present"
 */
export function extractKeywords(actualBehavior: string): string {
  // Common words to remove
  const commonWords = new Set([
    "the", "a", "an", "is", "are", "was", "were", "be", "been", "being",
    "have", "has", "had", "do", "does", "did", "will", "would", "should",
    "could", "may", "might", "must", "can", "in", "on", "at", "to", "for",
    "of", "with", "from", "by", "about", "as", "into", "through", "during",
    "before", "after", "above", "below", "between", "under", "again", "further",
    "then", "once", "here", "there", "when", "where", "why", "how", "all",
    "both", "each", "few", "more", "most", "other", "some", "such", "no",
    "nor", "not", "only", "own", "same", "so", "than", "too", "very", "s",
    "t", "just", "don", "now", "d", "ll", "m", "o", "re", "ve", "y",
  ]);

  // Split into words, filter common words, capitalize
  const words = actualBehavior
    .toLowerCase()
    .replace(/[^\w\s]/g, " ")  // Remove punctuation
    .split(/\s+/)
    .filter(word => word.length > 2 && !commonWords.has(word))
    .map(word => word.charAt(0).toUpperCase() + word.slice(1));

  // Take up to 8 most meaningful words
  return words.slice(0, 8).join(" ");
}

/**
 * Determines overall test result based on question responses.
 *
 * @param responses - User responses to test step questions (keys are q0, q1, etc. for combined or the question type for single)
 * @param questionTypes - Question type configuration from database
 * @returns Test result: "pass" | "fail" | "blocked" | "skip"
 */
export function determineOverallResult(
  responses: Record<string, any>,
  questionTypes: any
): "pass" | "fail" | "blocked" | "skip" {
  // If array (combined questions), all must pass for overall pass
  if (Array.isArray(questionTypes)) {
    const allPass = questionTypes.every((q, idx) => {
      const responseKey = `q${idx}`;
      const response = responses[responseKey];
      return evaluateSingleResponse(response, q) === "pass";
    });
    return allPass ? "pass" : "fail";
  }

  // Handle single question (response key is the question type)
  const response = responses[questionTypes.type];
  return evaluateSingleResponse(response, questionTypes);
}

/**
 * Evaluates a single question response.
 *
 * @param response - The response data for a single question
 * @param questionConfig - The question configuration
 * @returns Test result: "pass" | "fail" | "blocked" | "skip"
 */
function evaluateSingleResponse(
  response: any,
  questionConfig: any
): "pass" | "fail" | "blocked" | "skip" {
  const qType = questionConfig.type;

  if (qType === "yes_no") {
    return response?.answer === "yes" ? "pass" : "fail";
  }

  if (qType === "dropdown") {
    const selected = response?.selected;
    // Use template mapping if available
    if (questionConfig.template === "test_result") {
      if (selected === "Pass") return "pass";
      if (selected === "Fail") return "fail";
      if (selected === "Partial-UI" || selected === "Partial-Functionality") return "fail";
    }
    // Default: any non-"Pass" value is fail
    return selected === "Pass" ? "pass" : "fail";
  }

  if (qType === "checkbox") {
    // If categorization specified, use it
    if (questionConfig.categorization) {
      const hasUnselected = (response?.unselected?.length || 0) > 0;
      if (questionConfig.categorization.unselected === "fail" && hasUnselected) {
        return "fail";
      }
      return "pass";
    }
    // Default: all items selected = pass, any unselected = fail
    const hasUnselected = (response?.unselected?.length || 0) > 0;
    return hasUnselected ? "fail" : "pass";
  }

  if (qType === "pass_fail") {
    // Sprint 3 format: Pass/Fail buttons
    return response?.answer === "pass" ? "pass" : "fail";
  }

  // Unknown question type → default to pass
  return "pass";
}

/**
 * Gets pass rate color class based on percentage.
 *
 * @param passRate - Pass rate percentage (0-100)
 * @returns Tailwind color class for text/border
 */
export function getPassRateColor(passRate: number): string {
  if (passRate >= PASS_RATE_THRESHOLDS.EXCELLENT) return "text-green-600 border-green-600";
  if (passRate >= PASS_RATE_THRESHOLDS.GOOD) return "text-amber-600 border-amber-600";
  if (passRate >= PASS_RATE_THRESHOLDS.POOR) return "text-orange-600 border-orange-600";
  return "text-red-600 border-red-600";
}

/**
 * Gets pass rate background color for progress bars.
 *
 * @param passRate - Pass rate percentage (0-100)
 * @returns Tailwind background color class
 */
export function getPassRateBackgroundColor(passRate: number): string {
  if (passRate >= PASS_RATE_THRESHOLDS.EXCELLENT) return "bg-green-600";
  if (passRate >= PASS_RATE_THRESHOLDS.GOOD) return "bg-amber-600";
  if (passRate >= PASS_RATE_THRESHOLDS.POOR) return "bg-orange-600";
  return "bg-red-600";
}

// ============================================================================
// 14-STAGE WORKFLOW MODEL
// ============================================================================

/**
 * Workflow stages for 14-stage workflow model.
 * Maps to workflow_stage_enum in database.
 * Stage 12: Manual Testing (checklist execution, bug discovery)
 * Stage 13: Issue Resolution (fix bugs found in manual testing)
 * Stage 14: Sprint Complete (security review, deployment)
 */
export const WORKFLOW_STAGES = [
  // Planning Phase (Stages 1-3)
  {
    number: 1,
    name: "Sprint Analysis",
    gate: null,
    dbValue: "analysis",
    description: "Analyze sprint scope, review PRDs, understand schema"
  },
  {
    number: 2,
    name: "Architecture Planning",
    gate: "QA Audit",
    dbValue: "architecture",
    description: "Generate sprint plan, architecture doc, get QA approval"
  },
  {
    number: 3,
    name: "User Approval",
    gate: "User Approval",
    dbValue: "awaiting_approval",
    description: "User reviews and approves sprint plan before implementation"
  },

  // Implementation Phase (Stages 4-7)
  {
    number: 4,
    name: "Database Migration",
    gate: "GATE-1: Database Ready",
    dbValue: "database_migration",
    description: "Apply migrations, verify tables exist in Supabase"
  },
  {
    number: 5,
    name: "List Page Implementation",
    gate: null,
    dbValue: "list_implementation",
    description: "Implement list page with filters, pagination, sorting"
  },
  {
    number: 6,
    name: "Detail Page Implementation",
    gate: null,
    dbValue: "detail_implementation",
    description: "Implement multi-panel detail page with tabs"
  },
  {
    number: 7,
    name: "Forms & Edit",
    gate: null,
    dbValue: "forms_implementation",
    description: "Implement create/edit forms with validation"
  },

  // Testing Phase (Stages 8-11)
  {
    number: 8,
    name: "Visual Testing",
    gate: "GATE-3: Visual Preflight",
    dbValue: "visual_testing",
    description: "Playwright MCP: 5+ screenshots, 0 console errors, 0 blockers"
  },
  {
    number: 9,
    name: "Test Execution",
    gate: "GATE-2: Tests Executed",
    dbValue: "test_execution",
    description: "Run manual/automated tests, generate HTML report"
  },
  {
    number: 10,
    name: "Results Verification",
    gate: "GATE-4: Results Integrity",
    dbValue: "results_verification",
    description: "QA validates test artifacts and execution proof"
  },
  {
    number: 11,
    name: "Pass Rate Validation",
    gate: "GATE-5: Pass Rate Met",
    dbValue: "pass_rate_validation",
    description: "Verify >= 85% pass rate, 100% smoke tests"
  },

  // Manual Testing Phase (Stage 12)
  {
    number: 12,
    name: "Manual Testing",
    gate: null,
    dbValue: "manual_testing",
    description: "User executes manual testing checklist, discovers bugs"
  },

  // Issue Resolution Phase (Stage 13)
  {
    number: 13,
    name: "Issue Resolution",
    gate: null,
    dbValue: "issue_resolution",
    description: "Fix all issues found during manual testing"
  },

  // Completion Phase (Stage 14)
  {
    number: 14,
    name: "Sprint Complete",
    gate: "GATE-6: Security Review",
    dbValue: "complete",
    description: "Security scan, commit, close sprint"
  },
] as const;

/**
 * Type definition for workflow stage values from database.
 */
export type WorkflowStageValue = typeof WORKFLOW_STAGES[number]["dbValue"];

/**
 * Maps database workflow_stage_enum values to stage numbers.
 */
export const STAGE_NUMBER_MAP: Record<WorkflowStageValue, number> = {
  analysis: 1,
  architecture: 2,
  awaiting_approval: 3,
  database_migration: 4,
  list_implementation: 5,
  detail_implementation: 6,
  forms_implementation: 7,
  visual_testing: 8,
  test_execution: 9,
  results_verification: 10,
  pass_rate_validation: 11,
  manual_testing: 12,  // User executes manual testing checklist
  issue_resolution: 13,  // Fix bugs found in manual testing
  complete: 14,  // Sprint complete with GATE-6
};

/**
 * Maps gate numbers to their names and descriptions.
 */
export const GATE_DEFINITIONS = {
  1: { name: "Database Ready", description: "Tables exist in Supabase, migrations applied" },
  2: { name: "Tests Executed", description: "HTML report generated, test-results/ populated" },
  3: { name: "Visual Preflight", description: "5+ screenshots, 0 console errors, 0 blockers" },
  4: { name: "Results Integrity", description: "QA validates execution artifacts" },
  5: { name: "Pass Rate Met", description: ">= 85% overall, 100% smoke tests" },
  6: { name: "Security Review", description: "0 critical/high vulnerabilities, RLS enabled" },
} as const;

/**
 * Gate status color mapping.
 */
export const GATE_STATUS_COLORS = {
  passed: "bg-green-100 text-green-800 border-green-300",
  pending: "bg-yellow-100 text-yellow-800 border-yellow-300",
  failed: "bg-red-100 text-red-800 border-red-300",
  skipped: "bg-amber-100 text-amber-800 border-amber-300",
} as const;
